'use client'

import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { Badge, Cell, Divider, Icon, Spacer, Text } from '@vinted/web-ui'
import { ArrowLeft24, ChevronRight24 } from '@vinted/monochrome-icons'
import { compact } from 'lodash'
import { v4 as uuid } from 'uuid'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  flattenDynamicFilterOptions,
  transformDynamicFilterDefaultOptions,
  transformDynamicFilterGroupOptions,
  transformDynamicFilterNavigationalOptions,
  DynamicFilterOptionModel,
  FilterCatalogModel,
  LinksModel,
  FilterGroupModel,
  OptionType,
  SelectionType,
  TranslationModel,
} from '@marketplace-web/catalog/catalog-data'
import { useCatalogContext, clickEvent } from '@marketplace-web/catalog/catalog-page-data'
import {
  userClickDynamicFilterOptionEvent,
  userViewDynamicFilterOptionEvent,
} from '@marketplace-web/catalog/catalog-filters-data'

import FilterCatalogList from '../../../FilterCatalogList/FilterCatalogList'
import FilterCell from '../../../FilterCell'
import FilterCellSuffix from '../../../FilterCell/FilterCellSuffix'
import FilterGroupList from '../../../FilterGroupList/FilterGroupList'
import FilterModal from '../../../FilterModal'
import FilterModalNavigation from '../../../FilterModalNavigation'
import SelectableItemList, {
  RenderItemProps,
  SelectableListItem,
} from '../../../SelectableItemList'
import DynamicFilterCell from '../../common/DynamicFilterCell'
import FilterLoader from '../../common/FilterLoader'
import FilterNoResults from '../../common/FilterNoResults'

type ListOptionItem = SelectableListItem<DynamicFilterOptionModel>

type Props = {
  type: string
  title: string
  selectionType: SelectionType
  isSelectionHighlighted?: boolean
  isNewFilter?: boolean
  options: Array<DynamicFilterOptionModel>
  selectedIds: Array<number>
  fromScreen: string
  isFilterCellVisible: boolean
  rootOptionType: OptionType
  isHidden?: boolean
  links?: Array<LinksModel>
  totalItemsCount?: number | null
  totalCountMax?: number | null
  isAsyncFacet?: boolean
  isLazy?: boolean
  isLoading?: boolean
  translations?: TranslationModel
  onListOptionSelect: (id: number) => void
  clearSelectedOptions: (type: string, ids?: Array<number>) => void
  onSubmit: () => void
  onFilterCellClick: () => void
  onFilterCellView: () => void
  onModalOpen: () => void
  onModalClose: () => void
}

const ListFilterModal = ({
  type,
  title,
  selectionType,
  isSelectionHighlighted,
  isNewFilter,
  options,
  selectedIds,
  fromScreen,
  isFilterCellVisible,
  rootOptionType,
  isHidden,
  links,
  totalItemsCount,
  totalCountMax,
  isAsyncFacet,
  isLazy,
  isLoading,
  translations,
  onListOptionSelect,
  clearSelectedOptions,
  onSubmit,
  onFilterCellClick,
  onFilterCellView,
  onModalOpen,
  onModalClose,
}: Props) => {
  const translate = useTranslate()
  const { track } = useTracking()
  const {
    state: {
      items: { searchTrackingParams },
    },
  } = useCatalogContext()
  const { globalSearchSessionId, globalCatalogBrowseSessionId } = searchTrackingParams
  const searchSessionId = searchTrackingParams.sessionId
  const seenItems = useRef<Array<number>>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedCatalogCodes, setSelectedCatalogCodes] = useState<Array<string>>([])
  const [highlightedSelectedIds, setHighlightedSelectedIds] = useState<Array<number>>(selectedIds)
  const [filteringSessionId, setFilteringSessionId] = useState('')
  const screen = `${'dynamic_filter'}_${type}`
  const showNoResult = !!isLazy && options.length === 0

  const flattedOptions = useMemo(() => {
    return flattenDynamicFilterOptions(options)
  }, [options])

  const suffixText = useMemo(() => {
    return compact(
      selectedIds.map(selectedId => flattedOptions.find(option => option.id === selectedId)?.title),
    ).join(', ')
  }, [selectedIds, flattedOptions])

  const listItems = useMemo(() => {
    return transformDynamicFilterDefaultOptions(
      options,
      rootOptionType,
      isSelectionHighlighted,
      highlightedSelectedIds,
    )
  }, [options, rootOptionType, isSelectionHighlighted, highlightedSelectedIds])

  const navigationalItems = useMemo(() => {
    return transformDynamicFilterNavigationalOptions(
      options,
      rootOptionType,
      isSelectionHighlighted,
      highlightedSelectedIds,
    )
  }, [options, rootOptionType, isSelectionHighlighted, highlightedSelectedIds])

  const getGroupItems = (catalogCode: string) => {
    const currentCatalog = navigationalItems.find(({ code }) => code === catalogCode)
    if (currentCatalog) {
      return currentCatalog.matchingGroups
    }

    return transformDynamicFilterGroupOptions(
      options,
      rootOptionType,
      isSelectionHighlighted,
      highlightedSelectedIds,
    )
  }

  const closeModal = () => {
    setFilteringSessionId('')
    seenItems.current = []

    setIsModalOpen(false)
    onModalClose()
  }

  const openModal = () => {
    setSelectedCatalogCodes(prevCodes => [...prevCodes, OptionType.Root])
    setIsModalOpen(true)

    setHighlightedSelectedIds(selectedIds)
    onModalOpen()
  }

  const getCurrentPosition = (id: number) =>
    flattedOptions.findIndex(option => option.id === id) + 1

  const handleBackClick = (catalogCode: string) => () => {
    if (catalogCode === OptionType.Root) closeModal()

    seenItems.current = []
    setSelectedCatalogCodes(prevCodes => prevCodes.filter(prevCode => prevCode !== catalogCode))
    setHighlightedSelectedIds(selectedIds)
  }

  const handleItemClick = (id: number, position: number, itemsCount?: number) => {
    onListOptionSelect(id)

    track(
      userClickDynamicFilterOptionEvent({
        id,
        type,
        code: type,
        position,
        itemsCount,
        isSelected: !selectedIds.includes(id),
        filteringSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        searchSessionId,
        fromScreen,
      }),
    )
  }

  const handleDefaultItemClick = (item: ListOptionItem) => {
    const id = Number(item.id)
    const position = getCurrentPosition(id)
    const itemsCount = item.data?.itemsCount

    handleItemClick(id, position, itemsCount)
  }

  const handelGroupItemClick = (id: number) => {
    const position = getCurrentPosition(id)

    handleItemClick(id, position)
  }

  const handelNavItemClick = (catalog: FilterCatalogModel) => {
    setSelectedCatalogCodes(prevCodes => [...prevCodes, catalog.code])
  }

  const handleSubmitClick = () => {
    closeModal()
    onSubmit()

    track(
      clickEvent({
        screen,
        target: 'apply_filters',
      }),
    )
  }

  const clearSelectedOptionIds = (groupIds?: Array<number>) => () => {
    clearSelectedOptions(type, groupIds)

    track(
      clickEvent({
        target: 'clear_filters',
        screen,
      }),
    )
  }

  const handleFilterCellClick = () => {
    openModal()
    onFilterCellClick()
  }

  const getHandleItemView = (item: ListOptionItem) => (inView: boolean) => {
    if (!inView) return

    const itemId = Number(item.id)
    if (seenItems.current.includes(itemId)) return

    const position = getCurrentPosition(itemId)

    track(
      userViewDynamicFilterOptionEvent({
        id: itemId,
        type,
        code: type,
        position,
        itemsCount: item.data?.itemsCount,
        filteringSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        searchSessionId,
        fromScreen,
      }),
    )

    seenItems.current = [...seenItems.current, itemId]
  }

  useEffect(() => {
    if (isModalOpen) setFilteringSessionId(uuid())
  }, [isModalOpen])

  const renderTitle = () => (
    <>
      <Text text={title} type="title" as="span" />
      {isNewFilter && (
        <>
          <Spacer orientation="vertical" />
          <Badge theme="primary" content={translate('common.new_badge')} />
        </>
      )}
    </>
  )

  const renderModalNavigation =
    (currentTitle: string, catalogCode: string, groupIds?: Array<number>) => () => {
      return (
        <FilterModalNavigation
          title={currentTitle}
          icon={
            <Icon
              name={ArrowLeft24}
              color="greyscale-level-1"
              testId={`${type}-navigation-prefix-icon`}
            />
          }
          prefixAriaLabel={translate('catalog.filters.a11y.actions.back')}
          suffixText={!showNoResult && translate('catalog.filters.dynamic.clear')}
          onPrefixClick={handleBackClick(catalogCode)}
          onSuffixClick={clearSelectedOptionIds(groupIds)}
        />
      )
    }

  const renderCatalogCellSuffixIcon = () => <Icon name={ChevronRight24} color="greyscale-level-3" />

  const getSelectedSuffix = (groups: Array<FilterGroupModel<DynamicFilterOptionModel>>) => {
    const items = groups.flatMap(group => group.items)

    return compact(
      selectedIds.map(selectedId => items.find(({ id }) => selectedId === id)?.title),
    ).join(', ')
  }

  const renderCatalogCellSuffix = (groups: Array<FilterGroupModel<DynamicFilterOptionModel>>) => (
    <FilterCellSuffix
      suffixText={getSelectedSuffix(groups)}
      textTheme="primary"
      renderSuffix={renderCatalogCellSuffixIcon}
    />
  )

  const renderItemCell = ({
    item,
    itemElementProps,
  }: RenderItemProps<DynamicFilterOptionModel>) => (
    <DynamicFilterCell
      key={item.id}
      type={type}
      item={item}
      itemElementProps={itemElementProps}
      handleItemView={getHandleItemView(item)}
    />
  )

  const createFilterModal = (navigation: () => ReactNode, content: ReactNode, key?: string) => {
    let modalContent: ReactNode

    if (isLoading) {
      modalContent = <FilterLoader />
    } else if (showNoResult) {
      modalContent = <FilterNoResults translations={translations} />
    } else {
      modalContent = content
    }

    return (
      <FilterModal
        renderNavigation={navigation}
        isOpen={isModalOpen}
        key={key}
        totalItemsCount={totalItemsCount}
        totalCountMax={totalCountMax}
        isAsyncFacet={isAsyncFacet}
        hideSubmitButton={isLoading}
        isGoBack={showNoResult}
        onSubmit={handleSubmitClick}
        onGoBack={handleBackClick(OptionType.Root)}
        testId={`${type}-filter-modal`}
      >
        {modalContent}
      </FilterModal>
    )
  }
  const renderDefaultModal = () => {
    const navigation = renderModalNavigation(title, OptionType.Root)
    const content = (
      <>
        <SelectableItemList
          name={`${type}_ids`}
          items={listItems}
          selected={selectedIds}
          onItemClick={handleDefaultItemClick}
          renderItem={renderItemCell}
          isMultiSelect={selectionType === SelectionType.Multi}
          disableButtonClicks
        />
        <Divider />
        {links?.length && (
          <Cell>
            {links.map(link => (
              <Text key={`${type}_link`} html text={link.body} as="p" />
            ))}
          </Cell>
        )}
      </>
    )

    return createFilterModal(navigation, content)
  }

  const renderNavigationalItem = (catalogCode: string) => {
    let content: ReactNode
    const currentCatalog = navigationalItems.find(({ code }) => code === catalogCode)
    const currentTitle = currentCatalog?.title || title
    const groups = getGroupItems(catalogCode)
    const groupIds = groups?.flatMap(({ items }) => items.map(({ id }) => id)) || []

    const navigation = renderModalNavigation(currentTitle, catalogCode, groupIds)

    if (groups.length) {
      content = (
        <>
          <FilterGroupList
            name={`${type}_ids`}
            groups={groups}
            isMultiSelect={selectionType === SelectionType.Multi}
            selectedIds={selectedIds}
            onItemClick={handelGroupItemClick}
            renderItem={renderItemCell}
          />
          <Divider />
        </>
      )
    } else {
      content = (
        <>
          <FilterCatalogList
            catalogs={navigationalItems}
            onItemClick={handelNavItemClick}
            renderSuffix={renderCatalogCellSuffix}
          />
          <Divider />
        </>
      )
    }

    return createFilterModal(navigation, content, catalogCode)
  }

  const renderNavigationalModal = () =>
    selectedCatalogCodes.map(code => renderNavigationalItem(code))

  const renderModal = () => {
    if (rootOptionType === OptionType.Group || rootOptionType === OptionType.Navigational) {
      return renderNavigationalModal()
    }

    return renderDefaultModal()
  }

  if (isHidden) return null

  return (
    <>
      <FilterCell
        title={renderTitle()}
        suffixText={suffixText || translate('catalog.filters.dynamic.all')}
        suffixTextTheme={suffixText ? 'primary' : undefined}
        onClick={handleFilterCellClick}
        onView={onFilterCellView}
        visible={isFilterCellVisible}
      />
      {renderModal()}
    </>
  )
}

export default ListFilterModal
